import React, { useEffect, useState } from 'react';
import Table from './components/Table'; // Asumimos que la tabla es un componente reutilizable
import TableSearchBar from '../../../../../../../../components/common/Table/TableSearchBar';
import { BillType } from '../../../../types';
import { getAllLocalBillsByLocalIdAndRangeDate } from '../../../../../../../../crud/management_api/local_bills';
import MiniLoadingSpinner from '../../../../../../../../components/Spinners/MiniLoadingSpinner';
import DateFilter from '../../../../../../Caja/Receipts/components/Filters/DateFilter'; // Asegúrate de tener este componente
import { Button, Offcanvas, Form, InputGroup } from 'react-bootstrap';
import { FaFilter } from 'react-icons/fa'; // Icono de filtro
import { X } from 'react-bootstrap-icons';
import MultiSelectFilter from '../../../../../../../../components/common/MultiSelectFilter/MultiSelectFilter';
import ActiveFilter, { ActiveFilterItem } from '../../../../../../../../components/common/ActiveFilter/ActiveFilter';
import BillsHistoricSummary from './components/BillsHistoricSummary/BillsHistoricSummary';
import { LocalSession } from '../../../../../../../../types/LocalSession';

interface HistoricoProps {
  globalCategoriesList: any[];
  localCategoriesList: any[];
  fetchBills: () => void; // Función para obtener las facturas
  lastSession?: LocalSession | null; // Sesión local opcional
}

const Historico: React.FC<HistoricoProps> = ({
  globalCategoriesList,
  localCategoriesList,
  fetchBills,
  lastSession
}) => {
  const [billsList, setBillsList] = useState<BillType[]>([]);
  const [loading, setLoading] = useState(true);
  const [billType, setBillType] = useState<BillType[]>([]);
  const [filteredBillType, setFilteredBillType] = useState<BillType[]>([]);
  const [searchedBillType, setSearchedBillType] = useState<BillType[]>([]);

  const addDays = (date: Date, days: number) => {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d.toISOString().split('T')[0];
  };
  const now = new Date();
  const [startDate, setStartDate] = useState<string>(addDays(now, -89)); // 3 meses atrás (aprox 30 días x 3 - 1)
  const [endDate, setEndDate] = useState<string>(addDays(now, 0));

  const [showFilters, setShowFilters] = useState(false);

  const [selectedCategories, setSelectedCategories] = useState<any[]>([]);
  const [selectedAmountRange, setSelectedAmountRange] = useState<[number, number]>([0, 10000]);
  const [enableAmountFilter, setEnableAmountFilter] = useState<boolean>(false);
  const [selectedBillNames, setSelectedBillNames] = useState<any[]>([]);
  const [soloSesionActual, setSoloSesionActual] = useState(false);

  useEffect(() => {
    const userContext = localStorage.getItem('user');
    const localId = userContext != null ? JSON.parse(userContext).local_id : 0;
    setLoading(true);
    getAllLocalBillsByLocalIdAndRangeDate(localId, startDate, endDate)
      .then((response) => {
        if (response != null) {
          const formattedData = response.data.map((item: any) => ({
            ...item,
            local_bill_category_name: String(item.local_bill_category_name ?? ''),
            bill_name: String(item.bill_name ?? ''),
            bill_number: String(item.bill_number ?? ''),
            amount: Number(item.amount),
          }));
          setBillsList(formattedData);
          setBillType(formattedData);
          setFilteredBillType(formattedData);
          setSearchedBillType(formattedData);
        }
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching bills: ', error);
        setLoading(false);
      });
  }, []);

  // Sincronizar fechas cuando se activa el filtro de sesión actual
  useEffect(() => {
    if (soloSesionActual && lastSession) {
      const sessionStart = new Date(lastSession.session_start);
      const sessionEnd = lastSession.session_end ? new Date(lastSession.session_end) : new Date();
      
      setStartDate(sessionStart.toISOString().split('T')[0]);
      setEndDate(sessionEnd.toISOString().split('T')[0]);
    }
  }, [soloSesionActual, lastSession]);

  const applyFilters = () => {
    const filteredData = billType.filter((bill: BillType) => {
      const billDateStr = bill.bill_date?.split('T')[0] || bill.bill_date;
      const billDate = new Date(billDateStr);
      const start = new Date(startDate);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);

      const isWithinDateRange = billDate >= start && billDate <= end;

      // Filtro de sesión actual
      const isInCurrentSession = soloSesionActual && lastSession ? (() => {
        const billDateTime = new Date(bill.bill_date);
        const sessionStart = new Date(lastSession.session_start);
        const sessionEnd = lastSession.session_end ? new Date(lastSession.session_end) : new Date();
        return billDateTime >= sessionStart && billDateTime <= sessionEnd;
      })() : true;

      let categoryName = '';
      if (bill.global_bill_category_id) {
        categoryName = bill.global_bill_category_name || 'Categoría global no definida';
      } else if (bill.local_bill_category_id) {
        categoryName = bill.local_bill_category_name || 'Categoría local no definida';
      } else {
        categoryName = 'Sin categoría';
      }
      const isCategoryMatch = selectedCategories.length === 0 || selectedCategories.includes(categoryName);

      const isAmountInRange =
        !enableAmountFilter || (bill.amount >= selectedAmountRange[0] && bill.amount <= selectedAmountRange[1]);

      const isBillNameMatch = selectedBillNames.length === 0 || selectedBillNames.includes(bill.bill_name);

      return isWithinDateRange && isInCurrentSession && isCategoryMatch && isAmountInRange && isBillNameMatch;
    });

    setFilteredBillType(filteredData);
  };

  useEffect(() => {
    applyFilters();
  }, [startDate, endDate, selectedCategories, selectedAmountRange, selectedBillNames, billType, enableAmountFilter, soloSesionActual, lastSession]);

  useEffect(() => {
    setSearchedBillType(filteredBillType);
  }, [filteredBillType]);

  const handleStartDate = (date: string) => {
    setStartDate(date);
    setSoloSesionActual(false);
  };
  const handleEndDate = (date: string) => {
    setEndDate(date);
    setSoloSesionActual(false);
  };

  // Sincronizar fechas con soloSesionActual y lastSession
  useEffect(() => {
    if (soloSesionActual) {
      let start = '';
      if (lastSession && lastSession.session_start) {
        start = lastSession.session_start.split('T')[0];
      } else {
        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, '0');
        const dd = String(today.getDate()).padStart(2, '0');
        start = `${yyyy}-${mm}-${dd}`;
      }
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, '0');
      const dd = String(today.getDate()).padStart(2, '0');
      const todayStr = `${yyyy}-${mm}-${dd}`;
      setStartDate(start);
      setEndDate(todayStr);
    }
  }, [soloSesionActual, lastSession]);

  // Construir filtros activos (comportamiento igual a ReceiptsDisplay)
  const activeFilters: ActiveFilterItem[] = [];
  if (!soloSesionActual) {
    if (startDate) {
      const [y, m, d] = startDate.split('-');
      activeFilters.push({ label: `Desde: ${d}-${m}-${y}`, value: 'startDate' });
    }
    if (endDate) {
      const [y, m, d] = endDate.split('-');
      activeFilters.push({ label: `Hasta: ${d}-${m}-${y}`, value: 'endDate' });
    }
  }
  if (selectedCategories.length > 0) {
    selectedCategories.forEach((cat: string) => {
      activeFilters.push({ label: `Categoría: ${cat}`, value: `cat_${cat}` });
    });
  }
  if (selectedBillNames.length > 0) {
    selectedBillNames.forEach((name: string) => {
      activeFilters.push({ label: `Pago: ${name}`, value: `billname_${name}` });
    });
  }
  if (enableAmountFilter) {
    activeFilters.push({ label: `Monto: $${selectedAmountRange[0]} - $${selectedAmountRange[1]}`, value: 'amountRange' });
  }
  if (soloSesionActual) {
    activeFilters.push({ label: 'Solo sesión actual', value: 'soloSesionActual' });
  }

  // Función para eliminar filtros
  const handleRemoveFilter = (value: string) => {
    const todayStr = new Date().toISOString().split('T')[0];
    if (value === 'startDate') setStartDate(todayStr);
    else if (value === 'endDate') setEndDate(todayStr);
    else if (value.startsWith('cat_')) {
      const cat = value.replace('cat_', '');
      setSelectedCategories(selectedCategories.filter((c: string) => c !== cat));
    } else if (value.startsWith('billname_')) {
      const name = value.replace('billname_', '');
      setSelectedBillNames(selectedBillNames.filter((n: string) => n !== name));
    } else if (value === 'amountRange') setEnableAmountFilter(false);
    else if (value === 'soloSesionActual') setSoloSesionActual(false);
  };

  if (loading) {
    return <MiniLoadingSpinner />;
  }

  return (
    <>
      <div className="mp-page-header-container">
        <div className="buttons">
          <Button onClick={() => setShowFilters(true)} variant="none">
            Filtros <FaFilter />
          </Button>
        </div>
      </div>
      <Offcanvas show={showFilters} onHide={() => setShowFilters(false)} placement="end" className="mp-modal-slider">
        <Offcanvas.Header>
          <div className="form-modal-title-container">
            <Offcanvas.Title className="form-modal-title">Opciones de Filtrado</Offcanvas.Title>
          </div>
          <Button variant="none" className="modal-close-button" onClick={() => setShowFilters(false)}>
            <X size={24} />
          </Button>
        </Offcanvas.Header>
        <Offcanvas.Body style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 80px)' }}>
          <div className="receipt-displayer-filter-container">
            <DateFilter startDate={startDate} setStartDate={handleStartDate} endDate={endDate} setEndDate={handleEndDate} />

            <Form.Group className="mp-formgroup horizontal" style={{ width: '100%' }}>
              <MultiSelectFilter
                options={Array.from(
                  new Set(
                    billType.map((bill: any) => {
                      if (bill.global_bill_category_id) {
                        return bill.global_bill_category_name || 'Categoría global no definida';
                      } else if (bill.local_bill_category_id) {
                        return bill.local_bill_category_name || 'Categoría local no definida';
                      }
                      return 'Sin categoría';
                    })
                  )
                ).map((catName: string) => ({ value: catName, label: catName }))}
                onChange={(opts) => setSelectedCategories(opts ? opts.map(o => o.value) : [])}
                placeholder="Seleccionar categoría"
              />
            </Form.Group>

            <Form.Group className="mp-formgroup horizontal" style={{ width: '100%' }}>
              <MultiSelectFilter
                options={Array.from(new Set(billType.map(b => b.bill_name))).map(name => ({ value: name, label: name }))}
                onChange={(opts) => setSelectedBillNames(opts ? opts.map(o => o.value) : [])}
                placeholder="Seleccionar pago"
              />
            </Form.Group>

            <Form.Group className="mp-formgroup vertical" style={{width:'90%'}} controlId="enableAmountFilter">
              <Form.Check
                type="checkbox"
                label="Activar rango por monto"
                checked={enableAmountFilter}
                onChange={e => setEnableAmountFilter(e.target.checked)}
                className="mediopunto-form-check-input"
              />
            </Form.Group>

            {enableAmountFilter && (
              <Form.Group controlId="amountRange" className="mp-formgroup vertical amount_range">
                <Form.Label>Rango de Monto</Form.Label>
                <div className="d-flex justify-content-between">
                  <InputGroup className="mp-form-input-group">
                    <InputGroup.Text>$</InputGroup.Text>
                    <Form.Control
                      type="number"
                      value={selectedAmountRange[0]}
                      onChange={e => setSelectedAmountRange([Number(e.target.value), selectedAmountRange[1]])}
                      placeholder="Monto mínimo"
                    />
                  </InputGroup>
                  <span>-</span>
                  <InputGroup className="mp-form-input-group">
                    <InputGroup.Text>$</InputGroup.Text>
                    <Form.Control
                      type="number"
                      value={selectedAmountRange[1]}
                      onChange={e => setSelectedAmountRange([selectedAmountRange[0], Number(e.target.value)])}
                      placeholder="Monto máximo"
                    />
                  </InputGroup>
                </div>
              </Form.Group>
            )}

            <Form.Group className="mp-formgroup horizontal" controlId="soloSesionActual">
              <Form.Check
                type="checkbox"
                label="Solo sesión actual"
                checked={soloSesionActual}
                className="mediopunto-form-check-input"
                onChange={e => setSoloSesionActual(e.target.checked)}
              />
            </Form.Group>
          </div>
        </Offcanvas.Body>
      </Offcanvas>

      {filteredBillType.length === 0 && soloSesionActual ? (
        <h2 style={{ textAlign: 'center', marginTop: '2rem', color: 'var(--text-color)', fontSize: '1.2rem' }}>
          No existen datos en la sesión actual.<br />
          Por favor, actualice el filtro para ver otros resultados.
        </h2>
      ) : (
        <>
          <BillsHistoricSummary bills={searchedBillType} />
          <TableSearchBar
            data={filteredBillType}
            updateFilteredData={setSearchedBillType}
            searchFields={['local_bill_category_name', 'bill_name', 'bill_number']}
            placeholder="Buscar cuenta..."
          />
          {/* Filtros activos */}
          <ActiveFilter filters={activeFilters} onRemove={handleRemoveFilter} />
          <Table
            data={billsList}
            setData={setBillsList}
            globalCategoriesList={globalCategoriesList}
            localCategoriesList={localCategoriesList}
            fetchBills={fetchBills}
          />
        </>
      )}
    </>
  );
};

export default Historico;
